import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormFile, useFormInput } from 'cityads-v3/hooks/form'
import { ValidatorName } from 'cityads-v3/hooks/validators'
import {
  LegalSubjectId,
  AdvertisingLawForm,
  loadAdvertisingLawFormCount,
  loadAdvertisingLawForm,
  saveAdvertisingLawForm,
} from 'cityads-v3/reducers/advertisingLawForm'
import { GEO_ID_RUSSIA } from 'cityads-v3/reducers/offers'
import { AuthUser } from 'cityads-v3/types/auth'
import { RootState } from 'cityads-v3/types/reducer'
import { WelcomeFormData } from 'cityads-v3/types/welcomeform'
import { FormError } from 'cityads-v3/utils/apiUtils_ts'
import { usePreloadRegions } from 'cityads-v3/utils/useUtils'

const FORM_STEPS = [0, 1, 2] as const

export const FORM_STEP_COUNT = FORM_STEPS.length
export type FormStep = typeof FORM_STEPS[number]
export const MIN_FORM_STEP = Math.min(...FORM_STEPS) as FormStep
export const MAX_FORM_STEP = Math.max(...FORM_STEPS) as FormStep

const PSRN_LENGTH_BY_LEGAL_SUBJECT_ID = {
  [LegalSubjectId.legalEntity]: 13,
  [LegalSubjectId.soleProprietor]: 15,
  [LegalSubjectId.selfEmployed]: 0,
}

export function useAdvertisingMarkingFormCount(): number | null {
  const dispatch = useDispatch()

  const user = useSelector((state: RootState) => state.auth.user)
  const count = useSelector((state: RootState) => state.advertisingLawForm.count)
  const isLoadingCount = useSelector((state: RootState) => state.advertisingLawForm.isLoadingCount)

  useEffect(() => {
    if (user && count === null && !isLoadingCount) {
      dispatch(loadAdvertisingLawFormCount())
    }
  }, [dispatch, user, count, isLoadingCount])

  return count
}

export function useAdvertisingMarkingForm() {
  usePreloadRegions()

  const user = useSelector((state: RootState) => state.auth.user)
  const welcomeFormData = useSelector((state: RootState) => state.welcomeform.formData || null)
  const regionsById = useSelector((state: RootState) => state.offers.regionsById)
  const countries = useMemo(() => Object.keys(regionsById).map(key => regionsById[key]).filter(region => !region.items), [regionsById])

  const isSaving = useSelector((state: RootState) => state.advertisingLawForm.isSaving)

  const [activeStep, setActiveStep] = useState<FormStep>(MIN_FORM_STEP)

  const formFields = useFormFields({
    user,
    welcomeFormData,
  })

  const countryRegion = regionsById[formFields.country.value] ?? null

  const setValues = useSetFormValues(formFields)
  const setErrors = useSetFormErrors(formFields, setActiveStep)

  useLoadData(setValues)
  const saveData = useSaveData(formFields, setValues, setErrors)

  const goBack = useCallback(() => {
    if (activeStep > MIN_FORM_STEP) {
      setActiveStep(FORM_STEPS[activeStep - 1])
    }
  }, [activeStep, setActiveStep])

  const goNext = useCallback(() => {
    if (!formFields.validateByStep(activeStep)) { return }

    if (activeStep < MAX_FORM_STEP) {
      setActiveStep(FORM_STEPS[activeStep + 1])
    } else {
      saveData()
    }
  }, [activeStep, setActiveStep, formFields.validateByStep, saveData])


  return {
    formFields,
    activeStep,
    goBack,
    goNext,
    isSaving,
    countries,
    countryRegion,
  }
}

type useFormFieldsProps = {
  user: AuthUser | null,
  welcomeFormData: WelcomeFormData | null,
}

export function useFormFields({ user, welcomeFormData }: useFormFieldsProps) {
  const country = useFormInput<number>(['notEmpty'], {}, GEO_ID_RUSSIA)

  const lastName = useFormInput<string>(['notEmpty'], {}, welcomeFormData?.sname ?? '')
  const firstName = useFormInput<string>(['notEmpty'], {}, welcomeFormData?.fname ?? '')
  const middleName = useFormInput<string>([], {}, '')
  const legalSubject = useFormInput<LegalSubjectId>(['notEmpty'], {}, null)

  const psrnValidation: ValidatorName[] = legalSubject.value === LegalSubjectId.selfEmployed ? [] : ['notEmpty', 'onlyDigits']
  const psrnValidationParams = { onlyDigits: { allowedLength: [ PSRN_LENGTH_BY_LEGAL_SUBJECT_ID[legalSubject.value] ] } }

  const legalNameValidation: ValidatorName[] = legalSubject.value === LegalSubjectId.legalEntity ? ['notEmpty'] : []
  const legalAddressValidation: ValidatorName[] = legalSubject.value === LegalSubjectId.legalEntity ? ['notEmpty'] : []

  const legalName = useFormInput<string>(legalNameValidation, {}, '')
  const vatNumber = useFormInput<string>(['notEmpty', 'onlyDigits'], { onlyDigits: { allowedLength: [10, 12] } }, '')
  const psrn = useFormInput<string>(psrnValidation, psrnValidationParams, '')
  const legalAddress = useFormInput<string>(legalAddressValidation, {}, '')
  const email = useFormInput<string>(['notEmpty', 'emailAddress'], {}, user?.email ?? '')
  const websiteUrl = useFormInput<string>(['notEmpty', 'url'], {}, welcomeFormData?.traffic_type_url ?? '')
  const ownershipRights = useFormFile(['notEmpty'])

  useEffect(() => {
    if (legalSubject.value === LegalSubjectId.selfEmployed) {
      psrn.setValue('')
    }

    psrn.setError(null)
  }, [legalSubject.value, psrn.setValue, psrn.setError])

  const validateByStep = useCallback((step: FormStep) => {
    let validators: (ReturnType<typeof useFormInput>['validate'])[]

    switch (step) {
      case 0:
        validators = [country.validate]
        break
      case 1:
        validators = [lastName.validate, firstName.validate, middleName.validate, legalSubject.validate]
        break
      case 2:
        validators = [
          legalName.validate,
          vatNumber.validate,
          psrn.validate,
          legalAddress.validate,
          email.validate,
          websiteUrl.validate,
          ownershipRights.validate,
        ]
        break
    }

      return validators.map(validator => validator()).every(validationResult => validationResult)
  }, [
    country.validate,
    lastName.validate,
    firstName.validate,
    middleName.validate,
    legalSubject.validate,
    legalName.validate,
    vatNumber.validate,
    psrn.validate,
    legalAddress.validate,
    email.validate,
    websiteUrl.validate,
    ownershipRights.validate,
  ])

  return {
    country,
    lastName,
    firstName,
    middleName,
    legalSubject,
    legalName,
    vatNumber,
    psrn,
    legalAddress,
    email,
    websiteUrl,
    ownershipRights,
    validateByStep,
  }
}

export type FormFields = Omit<ReturnType<typeof useFormFields>, 'validateByStep'>

function useSetFormValues(formFields: FormFields) {
  return useCallback((data: AdvertisingLawForm) => {
    formFields.country.setValue( data.country_id )
    formFields.lastName.setValue( data.last_name )
    formFields.firstName.setValue( data.first_name )
    formFields.middleName.setValue( data.middle_name )
    formFields.legalSubject.setValue( data.legal_subject )
    formFields.legalName.setValue( data.legal_name )
    formFields.vatNumber.setValue( data.vat_number )
    formFields.psrn.setValue( data.psrn )
    formFields.legalAddress.setValue( data.legal_address )
    formFields.email.setValue( data.email )
    formFields.websiteUrl.setValue( data.website_url )
    formFields.ownershipRights.setValue( data.ownership_rights?.[0] )
  }, [
    formFields.country.setValue,
    formFields.lastName.setValue,
    formFields.firstName.setValue,
    formFields.middleName.setValue,
    formFields.legalSubject.setValue,
    formFields.legalName.setValue,
    formFields.vatNumber.setValue,
    formFields.psrn.setValue,
    formFields.legalAddress.setValue,
    formFields.email.setValue,
    formFields.websiteUrl.setValue,
    formFields.ownershipRights.setValue,
  ])
}

function joinErrors(errors: string[] | undefined): string {
  return errors?.join('\n') || undefined
}

function useSetFormErrors(formFields: FormFields, setActiveStep: (step: FormStep) => void) {
  return useCallback((formError: FormError | null) => {
    const errorMessages = {
      country        : joinErrors(formError?.errors.country_id),
      lastName       : joinErrors(formError?.errors.last_name),
      firstName      : joinErrors(formError?.errors.first_name),
      middleName     : joinErrors(formError?.errors.middle_name),
      legalSubject   : joinErrors(formError?.errors.legal_subject),
      legalName      : joinErrors(formError?.errors.legal_name),
      vatNumber      : joinErrors(formError?.errors.vat_number),
      psrn           : joinErrors(formError?.errors.psrn),
      legalAddress   : joinErrors(formError?.errors.legal_address),
      email          : joinErrors(formError?.errors.email),
      websiteUrl     : joinErrors(formError?.errors.website_url),
      ownershipRights: joinErrors(formError?.errors.ownership_rights),
    }

    formFields.country.setError(errorMessages.country)
    formFields.lastName.setError(errorMessages.lastName)
    formFields.firstName.setError(errorMessages.firstName)
    formFields.middleName.setError(errorMessages.middleName)
    formFields.legalSubject.setError(errorMessages.legalSubject)
    formFields.legalName.setError(errorMessages.legalName)
    formFields.vatNumber.setError(errorMessages.vatNumber)
    formFields.psrn.setError(errorMessages.psrn)
    formFields.legalAddress.setError(errorMessages.legalAddress)
    formFields.email.setError(errorMessages.email)
    formFields.websiteUrl.setError(errorMessages.websiteUrl)
    formFields.ownershipRights.setError(errorMessages.ownershipRights)

    if (errorMessages.country) {
      setActiveStep(0)
    } else if (
      errorMessages.lastName ||
      errorMessages.firstName ||
      errorMessages.middleName ||
      errorMessages.legalSubject
    ) {
      setActiveStep(1)
    } else if (
      errorMessages.legalName ||
      errorMessages.vatNumber ||
      errorMessages.psrn ||
      errorMessages.legalAddress ||
      errorMessages.email ||
      errorMessages.websiteUrl ||
      errorMessages.ownershipRights
    ) {
      setActiveStep(2)
    }
  }, [
    formFields.country.setError,
    formFields.lastName.setError,
    formFields.firstName.setError,
    formFields.middleName.setError,
    formFields.legalSubject.setError,
    formFields.legalName.setError,
    formFields.vatNumber.setError,
    formFields.psrn.setError,
    formFields.legalAddress.setError,
    formFields.email.setError,
    formFields.websiteUrl.setError,
    formFields.ownershipRights.setError,
    setActiveStep,
  ])
}

function useLoadData(setFormValues: ReturnType<typeof useSetFormValues>) {
  const dispatch = useDispatch()

  useEffect(() => {
    const action = async () => {
      const data = await dispatch(loadAdvertisingLawForm())

      if (data) {
        setFormValues(data)
      }
    }

    action()
  }, [dispatch, setFormValues])
}

function useSaveData(
  formFields: FormFields,
  setFormValues: ReturnType<typeof useSetFormValues>,
  setFormErrors: ReturnType<typeof useSetFormErrors>
) {
  const dispatch = useDispatch()

  return useCallback(async () => {
    try {
      const savedForm = await dispatch(saveAdvertisingLawForm({
        country_id      : formFields.country.value,
        last_name       : formFields.lastName.value,
        first_name      : formFields.firstName.value,
        middle_name     : formFields.middleName.value,
        legal_subject   : formFields.legalSubject.value,
        legal_name      : formFields.legalName.value,
        vat_number      : formFields.vatNumber.value,
        psrn            : formFields.psrn.value,
        legal_address   : formFields.legalAddress.value,
        email           : formFields.email.value,
        website_url     : formFields.websiteUrl.value,
        ownership_rights: formFields.ownershipRights.value ? [formFields.ownershipRights.value] : [],
      }))

      setFormValues(savedForm)
      setFormErrors(null)

      return savedForm
    } catch (err) {
      if (err instanceof FormError) {
        setFormErrors(err)
      }

      throw err
    }
  }, [
    dispatch,
    formFields.country.value,
    formFields.lastName.value,
    formFields.firstName.value,
    formFields.middleName.value,
    formFields.legalSubject.value,
    formFields.legalName.value,
    formFields.vatNumber.value,
    formFields.psrn.value,
    formFields.legalAddress.value,
    formFields.email.value,
    formFields.websiteUrl.value,
    formFields.ownershipRights.value,
    setFormValues,
    setFormErrors,
  ])
}
